import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import api, { errMsg } from "../../lib/api";
import { PageHeader } from "../../components/bits";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";

const CONFIGS = {
  signoff: {
    type: "requirements_signoff", title: "Requirements Agreement & Sign-off", overline: "Scope",
    fields: [
      { key: "rtmVersion", label: "RTM Version" },
      { key: "dateOfAgreement", label: "Date of Agreement", type: "date" },
      { key: "totalRequirementsAgreed", label: "Total Requirements Agreed", type: "number" },
    ],
    parties: ["clientRepresentative", "clientSponsor", "tmrRepresentative"],
    partyLabels: { clientRepresentative: "Client Representative", clientSponsor: "Client Sponsor", tmrRepresentative: "Vendor Representative" },
  },
  closeout: {
    type: "closeout_report", title: "Project Closeout Report", overline: "Transition",
    fields: [
      { key: "status", label: "Status" },
      { key: "projectSummary", label: "Project Summary", textarea: true },
      { key: "objectivesVsOutcomes", label: "Objectives vs Outcomes", textarea: true },
      { key: "deliverablesAccepted", label: "Deliverables Accepted", textarea: true },
      { key: "schedulePerformance", label: "Schedule Performance", textarea: true },
      { key: "budgetPerformance", label: "Budget Performance", textarea: true },
      { key: "outstandingItems", label: "Outstanding Items", textarea: true },
      { key: "lessonsLearnedWell", label: "Lessons Learned — What went well", textarea: true },
      { key: "lessonsLearnedImprove", label: "Lessons Learned — What to improve", textarea: true },
      { key: "closureDate", label: "Closure Date", type: "date" },
    ],
    parties: [],
  },
};

export default function SingletonPage({ kind }) {
  const { projectId } = useParams();
  const cfg = CONFIGS[kind];
  const [doc, setDoc] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/projects/${projectId}/singletons/${cfg.type}`).then((r) => setDoc(r.data || {})).catch((e) => toast.error(errMsg(e)));
  }, [projectId, cfg.type]);

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put(`/projects/${projectId}/singletons/${cfg.type}`, doc);
      setDoc(data);
      toast.success("Saved");
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  if (doc === null) return <div className="text-muted-foreground">Loading…</div>;

  const setParty = (party, key, val) => setDoc({ ...doc, [party]: { ...(doc[party] || {}), [key]: val } });

  return (
    <div data-testid={`singleton-page-${kind}`}>
      <PageHeader overline={cfg.overline} title={cfg.title}
        actions={<Button data-testid={`save-${kind}-btn`} className="rounded-none" onClick={save} disabled={busy}>{busy ? "Saving…" : "Save"}</Button>} />
      <div className="border border-border bg-card p-5 max-w-3xl space-y-4">
        {cfg.fields.map((fld) => (
          <div key={fld.key}>
            <Label className="text-xs uppercase tracking-wide">{fld.label}</Label>
            {fld.textarea ? (
              <Textarea data-testid={`field-${fld.key}`} rows={3} value={doc[fld.key] ?? ""} onChange={(e) => setDoc({ ...doc, [fld.key]: e.target.value })} />
            ) : (
              <Input data-testid={`field-${fld.key}`} type={fld.type || "text"} value={doc[fld.key] ?? ""} onChange={(e) => setDoc({ ...doc, [fld.key]: fld.type === "number" ? Number(e.target.value) : e.target.value })} />
            )}
          </div>
        ))}
        {cfg.parties.length > 0 && (
          <div className="grid sm:grid-cols-3 gap-4 pt-2">
            {cfg.parties.map((party) => (
              <div key={party} className="border border-border p-3 space-y-2">
                <div className="overline-label">{cfg.partyLabels[party]}</div>
                <div><Label className="text-xs">Name</Label><Input value={doc[party]?.name ?? ""} onChange={(e) => setParty(party, "name", e.target.value)} /></div>
                <div><Label className="text-xs">Title</Label><Input value={doc[party]?.title ?? ""} onChange={(e) => setParty(party, "title", e.target.value)} /></div>
                <div><Label className="text-xs">Signed date</Label><Input type="date" value={doc[party]?.date ?? ""} onChange={(e) => setParty(party, "date", e.target.value)} /></div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
